import { useEffect, useState } from 'react'
import './TabSystem.scss'
import Calendar from './Calendar/Calendar'
import RecipeList from './Recipes/RecipeList'
import type { User } from '../../utils/loginUtils'
import Header from '../Header'
import { getMessage } from '../../utils/messageUtils'
import ProductList from './Products/ProductList'
import Storage from './Storage/Storage'
import ShoppingList from './ShoppingList/ShoppingList'
import type { Mode } from '../../App'
import Template from './Templates/Template'
import Users from './Users/Users'

interface TabSystemProps {
  user: User
  isMobile: boolean
  handleLogout: () => void
  setMode: (mode: Mode) => void
}

const defaultTab = 'calendar'
function TabSystem({ user, isMobile, handleLogout, setMode }: TabSystemProps) {
  const [activeTab, setActiveTab] = useState<string>(() => {
    const savedTab = localStorage.getItem('activeTab')
    return savedTab ? JSON.parse(savedTab) : defaultTab
  })

  useEffect(() => {
    localStorage.setItem('activeTab', JSON.stringify(activeTab))
  }, [activeTab])

  const tabs = [
    {
      id: 'calendar',
      label: getMessage('calendar'),
      content: <Calendar user={user} isMobile={isMobile} setMode={setMode} />,
    },
    {
      id: 'recipes',
      label: getMessage('recipes'),
      content: <RecipeList user={user} isMobile={isMobile} setMode={setMode} />,
    },
    {
      id: 'products',
      label: getMessage('products'),
      content: <ProductList user={user} isMobile={isMobile} />,
    },
    {
      id: 'storage',
      label: getMessage('storage'),
      content: <Storage user={user} isMobile={isMobile} />,
    },
    {
      id: 'shoppingList',
      label: getMessage('shopping_list'),
      content: <ShoppingList user={user} isMobile={isMobile} />,
    },
    {
      id: 'templates',
      label: getMessage('templates'),
      content: <Template user={user} isMobile={isMobile} />,
    },
    {
      id: 'users',
      label: user.admin ? getMessage('users') : getMessage('user'),
      content: <Users currentUser={user} isMobile={isMobile} />,
    },
  ]

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content

  return (
    <div className="tab-system-root">
      <Header
        isMobile={isMobile}
        tabs={tabs}
        activeTab={activeTab}
        onTabSelect={setActiveTab}
        onLogout={handleLogout}
      />
      <div
        className={`tab-system-container ${isMobile ? 'tab-system-container-mobile' : ''}`}
      >
        <div
          className={`tab-system-card ${isMobile ? 'tab-system-card-mobile' : ''}`}
        >
          {/* Tab Navigation */}
          {!isMobile && (
            <div className="tab-navigation">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">{activeTabContent}</div>
        </div>
      </div>
    </div>
  )
}

export default TabSystem
